<template>
  <div class="bg-white p-6 rounded-lg shadow-md border border-gray-100 dark:border-gray-800 dark:bg-gray-800 dark:text-white">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-lg font-semibold">License States</h3>
      <GlassSelect
        v-model="currentView"
        @change="saveViewPreference"
        :options="[
          { value: 'doughnut', label: 'Doughnut Chart' },
          { value: 'bar', label: 'Bar Chart' },
          { value: 'numbers', label: 'Numbers View' },
        ]"
        :valueKey="'value'"
        :labelKey="'label'"
        placeholder="Select View"
        class="w-auto"
      />
    </div>

    <!-- Numbers View -->
    <div v-if="currentView === 'numbers'" class="space-y-4">
      <div
        v-for="(item, index) in chartData"
        :key="index"
        class="flex items-center justify-between"
      >
        <span class="text-gray-600 dark:text-white">{{ item.name }}</span>
        <span class="text-gray-900 dark:text-white font-medium">
          {{ item.total }}/{{ item.overall }}
        </span>
      </div>
      <div class="pt-2 border-t border-gray-200">
        <div class="flex items-center justify-between">
          <span class="text-gray-600 dark:text-white font-semibold">Total Components</span>
          <span class="text-gray-900 dark:text-white font-semibold">{{ totalComponents }}</span>
        </div>
      </div>
    </div>

    <!-- Doughnut Chart View -->
    <div v-else-if="currentView === 'doughnut'" class="h-80">
      <Doughnut :data="doughnutData" :options="doughnutOptions" />
    </div>

    <!-- Bar Chart View -->
    <div v-else class="h-80">
      <Bar :data="barData" :options="chartOptions" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import Cookies from "js-cookie";
import GlassSelect from "@/components/GlassSelect.vue";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Doughnut } from "vue-chartjs";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels
);

const props = defineProps({
  workspaceData: {
    type: Object,
    required: true,
    default: () => ({
        active: { total: 0},
        unapproved: { total: 0 },
        hardstop: { total: 0 },
        pending: { total: 0 },
    }),
  },
});

// Initialize currentView and cookie management
const currentView = ref("doughnut");
const COOKIE_NAME = "preferred-infrastructure-view";
const COOKIE_EXPIRES = 30; // Days until cookie expires

// Load preferred view from cookie on mount
onMounted(() => {
  const savedView = Cookies.get(COOKIE_NAME);
  if (savedView && ["doughnut", "bar", "numbers"].includes(savedView)) {
    currentView.value = savedView;
  }
});

// Save view preference to cookie
const saveViewPreference = () => {
  Cookies.set(COOKIE_NAME, currentView.value, { expires: COOKIE_EXPIRES });
};

// Colors for different components
const colors = {
  active: "#22C55E", // green-500
  unapproved: "#3B82F6", // blue-500
  hardstop: "#8B5CF6", // purple-500
  pending: "#5B5CF6",
};

// Transform data for charts
const chartData = computed(() => [
  {
    name: "Active",
    total: props.workspaceData.license_states.active.total || 0,
    overall: props.workspaceData.agent_stats.total,
    color: colors.active,
  },
  {
    name: "Unapproved",
    total: props.workspaceData.license_states.unapproved.total || 0,
    overall: props.workspaceData.agent_stats.total,
    color: colors.unapproved,
  },
  {
    name: "Hardstop",
    total: props.workspaceData.license_states.hardstop.total || 0,
    overall: props.workspaceData.agent_stats.total,
    color: colors.hardstop,
  },
  {
    name: "Pending",
    total: props.workspaceData.license_states.pending.total || 0,
    overall: props.workspaceData.agent_stats.total,
    color: colors.pending,
  },
]);

const totalComponents = computed(() =>
  chartData.value.reduce((sum, item) => sum + item.total, 0)
);

// Calculate max value for y-axis scaling
const maxValue = computed(() =>
  Math.max(...chartData.value.map((item) => item.total))
);
const suggestedMax = computed(() => Math.ceil(maxValue.value * 1.2));

// Doughnut Chart Data and Options
const doughnutData = computed(() => ({
  labels: chartData.value.map((item) => item.name),
  datasets: [
    {
      data: chartData.value.map((item) => item.total),
      backgroundColor: chartData.value.map((item) => item.color),
      borderColor: "white",
      borderWidth: 2,
    },
  ],
}));

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "65%",
  plugins: {
    legend: {
      position: "right",
      labels: {
        usePointStyle: true,
        padding: 20,
        font: { size: 12 },
      },
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          const item = chartData.value[context.dataIndex];
          const percentage = ((item.total / item.overall) * 100).toFixed(1);
          return `${item.total} total (${percentage}%)`;
        },
      },
    },
    datalabels: {
      color: "rgb(17 24 39)",
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      borderRadius: 4,
      padding: { left: 4, right: 4, top: 2, bottom: 2 },
      font: {
        weight: "bold",
        size: 13,
      },
      formatter: (value, ctx) => {
        const item = chartData.value[ctx.dataIndex];
        const percentage = ((item.total / item.overall) * 100).toFixed(1);
        return value > 0 ? `${item.name}\n${percentage}%` : "";
      },
      textAlign: "center",
    },
  },
};

// Bar Chart Data and Options
const barData = computed(() => ({
  labels: chartData.value.map((item) => item.name),
  datasets: [
    {
      label: "Total",
      data: chartData.value.map((item) => item.total),
      backgroundColor: "#E5E7EB", // gray-200
      borderColor: "#E5E7EB",
      borderWidth: 1,
      barPercentage: 0.6,
    },
  ],
}));

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: "top",
    },
    tooltip: {
      callbacks: {
        label: function (context) {
          return `${context.dataset.label}: ${context.raw.toLocaleString()}`;
        },
      },
    },
    datalabels: {
      anchor: "end",
      align: "top",
      offset: 4,
      backgroundColor: "rgba(255, 255, 255, 0.8)",
      borderRadius: 4,
      padding: { left: 4, right: 4, top: 2, bottom: 2 },
      color: "rgb(17 24 39)",
      font: {
        weight: "bold",
        size: 12,
      },
      formatter: (value) => value.toLocaleString(),
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        precision: 0,
      },
      grid: {
        display: true,
      },
      suggestedMax: suggestedMax.value,
    },
    x: {
      grid: {
        display: false,
      },
    },
  },
}));
</script>